import {
  Box,
  Text,
  Flex,
  useDisclosure,
  Select,
  Button,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import SearchInput from "@root/components/common/SearchInput";
import { DataTable } from "@root/components";
import { CourseTableColumns } from "@root/components/kpec-admin/subscriber-management/coursestablecolumns";
import { useEffect, useState, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { RegisterSubscriberButton } from "./style";
import { AddSubscriberModal } from "@root/components/kpec-admin/subscriber-management/addsubscriberModal";
import { PlusIcon } from "@root/assets/admin/icons/plusicon";
import { ExcelIcon } from "@root/assets/icons/ExcelIcon";
import { DownloadIcon } from "@root/assets/icons/downloadIcon";

import {
  DrawerContainer,
  DrawerHeader,
  CourseTitleSection,
  CompletionBadge,
  DrawerClose,
  DownloadButton,
  EnrolledCard,
  AvatarBox,
  ProgressBackground,
  ProgressFill,
} from "@root/components/styles";
import { useSubscriberContext } from "@root/context/use-subscriber-context";
import { RECORDS_PER_PAGE } from "@root/constants";
import { useJournalistContext } from "@root/context/journalist-context";
import CustomSelect from "@root/components/common/CustomSelect";
import { useCertificateContext } from "@root/context/certificate-context";
import DocumentWithLabelIcon from "@root/assets/icons/document";
import { StyledProfileAvatar } from "@root/components/dashboard/sidebar/style";

const SubscriberCourses = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation("common");
  const { userid } = useParams<{ userid: string }>();
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const location = useLocation();
  const subscriberName = location.state?.companyName;

  const [selectedDepartmentTitle, setSelectedDepartmentTitle] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState<any>(null);

  const [departmentPage, setDepartmentPage] = useState(1);
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [hasMoreDepartments, setHasMoreDepartments] = useState(true);

  const [rolePage, setRolePage] = useState(1);
  const [roleLoading, setRoleLoading] = useState(false);
  const [hasMoreRoles, setHasMoreRoles] = useState(true);

  const {
    pageNo,
    setPageNo,
    pageCount,
    fetchSubscriberCourses,
    subscriberCourses,
    enrolledmembers,
    fetchEnrolledUsers,
  } = useSubscriberContext();
  const pageSize = RECORDS_PER_PAGE;
  const { fetchDepartments, departmentsData } = useJournalistContext();
  const {
    fetchUserInCsv,
    fetchMultiCertificates,
    fetchCertificate,
    isCourseLoading,
  } = useCertificateContext();

  const {
    isOpen: isDrawerOpen,
    onOpen: openDrawer,
    onClose: closeDrawer,
  } = useDisclosure();

  const handleCourseClick = (course: any) => {
    const courseId = course?.course?.id;
    const payload: any = {
      offset: 0,
      limit: pageSize,
    };

    if (selectedDepartment?.id) {
      payload.departmentId = selectedDepartment.id;
    }
    if (selectedRole) {
      const selectedRoleData = selectedDepartment?.children?.find(
        (role: any) => role.title === selectedRole
      );
      if (selectedRoleData?.id) {
        payload.roleId = selectedRoleData.id;
      }
    }

    fetchEnrolledUsers(courseId, userid, payload);
    setSelectedCourse(course);
    openDrawer();
  };

  const handleDownloadCSV = () => {
    const payload = {
      courseId: selectedCourse?.course?.id,
      subscriberId: userid,
    };
    fetchUserInCsv(payload);
  };

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);
    const payload = {
      offset: (newPage - 1) * pageSize,
      limit: pageSize,
      search: searchTerm || "",
    };
    fetchSubscriberCourses(userid, payload);
  };

  const payload = {
    offset: 0,
    limit: pageSize,
    search: searchTerm || "",
  };

  const handleDepartmentChange = (value: string) => {
    setSelectedDepartmentTitle(value);
    setSelectedRole("");
    const dept = departmentsData?.rows?.find((d: any) => d.title === value);
    setSelectedDepartment(dept);

    // Reset role pagination
    setRolePage(1);
    setHasMoreRoles(true);

    // Fetch enrolled users with department filter
    if (selectedCourse) {
      const courseId = selectedCourse?.course?.id;
      const payload: any = {
        offset: 0,
        limit: pageSize,
      };

      if (dept?.id) {
        payload.departmentId = dept.id;
      }

      fetchEnrolledUsers(courseId, userid, payload);
    }
  };
  const hasEnrolledMembers = enrolledmembers?.rows?.length > 0;

  const handleRoleChange = (value: string) => {
    setSelectedRole(value);

    // Fetch enrolled users with role filter
    if (selectedCourse) {
      const courseId = selectedCourse?.course?.id;
      const payload: any = {
        offset: 0,
        limit: pageSize,
      };

      if (selectedDepartment?.id) {
        payload.departmentId = selectedDepartment.id;
      }

      if (value) {
        const selectedRoleData = selectedDepartment?.children?.find(
          (role: any) => role.title === value
        );
        if (selectedRoleData?.id) {
          payload.roleId = selectedRoleData.id;
        }
      }

      fetchEnrolledUsers(courseId, userid, payload);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchSubscriberCourses(userid, payload).then(() => {
      setLoading(false);
    });
    fetchDepartments({
      subscriberId: userid,
      page: 1,
      limit: 20,
    });
  }, []);
  const loadMoreDepartments = async () => {
    if (departmentLoading || !hasMoreDepartments) return;

    setDepartmentLoading(true);
    const nextPage = departmentPage + 1;

    try {
      const result = await fetchDepartments({
        subscriberId: userid,
        page: nextPage,
        limit: 20,
      });
      if (result?.rows?.length < 20) {
        setHasMoreDepartments(false);
      }

      setDepartmentPage(nextPage);
    } catch (error) {
      console.error("Error loading more departments:", error);
    } finally {
      setDepartmentLoading(false);
    }
  };

  const loadMioreRoles = async () => {
    if (roleLoading || !hasMoreRoles || !selectedDepartment) return;

    setRoleLoading(true);
    const nextPage = rolePage + 1;

    try {
      const result = await fetchDepartments({
        subscriberId: userid,
        departmentId: selectedDepartment.id,
        page: nextPage,
        limit: 20,
      });

      if (result?.children?.length < 20) {
        setHasMoreRoles(false);
      }

      setRolePage(nextPage);
    } catch (error) {
      console.error("Error loading more roles:", error);
    } finally {
      setRoleLoading(false);
    }
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setPageNo(1);
    const payload = {
      offset: 0,
      limit: pageSize,
      search: newSearchTerm || "",
    };
    fetchSubscriberCourses(userid, payload);
  };
  const hasCompletedUser = enrolledmembers?.overallProgress > 0;
  return (
    <Flex direction="column" height="100%" bg="white" padding={"24px 16px"}>
      <Box display="flex" justifyContent="space-between" mt="4">
        <Box>
          <Text fontWeight={600} color="#2F2F34" fontSize="24px" mb="10px">
            {t("subscriberManagement.title")}
          </Text>
          <Text color="#798692" maxWidth="600px">
            {t("subscriberManagement.description")}
          </Text>
        </Box>
        <RegisterSubscriberButton onClick={onOpen}>
          <PlusIcon boxSize={5} color="#FFFFFF" />
          {t("subscriberManagement.registerNewSubscriber")}
        </RegisterSubscriberButton>
        <AddSubscriberModal isOpen={isOpen} onClose={onClose} />
      </Box>

      <Flex justifyContent="space-between" my={10}>
        <SearchInput
          placeholder={t("subscriberManagement.findacourse")}
          value={searchTerm}
          width="500px"
          onChange={handleSearchChange}
        />
      </Flex>
      <Box>
        <Text mb={1} fontSize="16px" fontWeight="500" color="#2F2F34">
          {subscriberName}{" "}
          <span style={{ color: "#1819B4" }}>{`> ${t(
            "subscriberManagement.registeredCourses"
          )}`}</span>
        </Text>
      </Box>

      {loading ? (
        <Text>{t("subscriberManagement.loading")}</Text>
      ) : subscriberCourses?.length === 0 ? (
        <Text color={"gray"}>{t("subscriberManagement.noCoursesFound")}</Text>
      ) : (
        <DataTable
          columns={CourseTableColumns(handleCourseClick, t)}
          data={subscriberCourses}
          showPagination={true}
          page={pageNo}
          pageCount={pageCount}
          pageSize={pageSize}
          handlePageChange={handlePageChange}
          theadBg="#dad5f5"
          rowHoverBg="gray.50"
          onRowClick={handleCourseClick}
        />
      )}
      {/*Drawer*/}
      {isDrawerOpen && (
        <Flex {...DrawerContainer}>
          <Flex {...DrawerHeader}>
            <Flex {...CourseTitleSection}>
              <Text fontSize="xl" fontWeight="bold" color="#2F2F34">
                {selectedCourse?.course?.title}
              </Text>
              <Flex {...CompletionBadge}>
                {Number(enrolledmembers?.overallProgress || 0).toFixed(3)}%{" "}
                {t("certificateSection.completed")}
              </Flex>
            </Flex>

            <Text {...DrawerClose} onClick={closeDrawer}>
              ×
            </Text>
          </Flex>

          <Flex
            justifyContent="space-between"
            mb={6}
            gap={3}
            flexWrap="wrap"
            alignItems="flex-start"
            minHeight="44px"
          >
            <Flex
              gap={2}
              alignItems="center"
              flexWrap="wrap"
              flex="1"
              minWidth="0"
            >
              <CustomSelect
                placeholder={t("coursesPage.department")}
                value={selectedDepartmentTitle}
                onChange={handleDepartmentChange}
                options={
                  departmentsData?.rows?.map((dept: any) => ({
                    value: dept.title,
                    label: dept.title,
                  })) || []
                }
                width="160px"
                height="44px"
                isDisabled={false}
              />
              <CustomSelect
                placeholder={t("coursesPage.role")}
                value={selectedRole}
                onChange={handleRoleChange}
                options={
                  selectedDepartment?.children?.map((role: any) => ({
                    value: role.title,
                    label: role.title,
                  })) || []
                }
                width="fit-content"
                height="44px"
                fontSize="14px"
                isDisabled={!selectedDepartment}
              />
            </Flex>
            <Box
              {...DownloadButton}
              flexShrink={0}
              onClick={hasEnrolledMembers ? handleDownloadCSV : undefined}
              cursor={hasEnrolledMembers ? "pointer" : "not-allowed"}
              borderColor={hasEnrolledMembers ? "#1819B4" : "#CDD6DC"}
              color={hasEnrolledMembers ? "#1819B4" : "#A7B2BA"}
              height="44px"
            >
              <ExcelIcon boxSize="4" marginRight="10px" color="#1819B4" />
              {t("subscriberManagement.downloadCSV")}
            </Box>
            <Button
              border={"1px solid #1819B4"}
              borderRadius="8px"
              fontSize="14px"
              color={hasCompletedUser ? "#1819B4" : "#A7B2BA"}
              variant="outline"
              height={"44px"}
              fontWeight={500}
              width="fit-content"
              _hover={{ bg: "#A7B2BA" }}
              isDisabled={!hasCompletedUser}
              onClick={() =>
                fetchMultiCertificates({
                  courseId: selectedCourse?.course?.id,
                  subscriberId: userid,
                  gmtOffset: 0,
                })
              }
            >
              {<DocumentWithLabelIcon boxSize={24} />}
              <Box ml={2} color="#1819B4">
                {t("courseDrawer.export")}
              </Box>
            </Button>
          </Flex>

          <Box>
            <Text fontSize="md" fontWeight="semibold" mb={4}>
              {t("subscriberManagement.enrolledUsers")}
            </Text>

            {/* Show "No enrolled users" message when there are no enrolled members */}
            {!hasEnrolledMembers ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                py={10}
                color="#798692"
              >
                <Text fontSize="md" fontWeight="500">
                  {t("subscriberManagement.noEnrolledUsers")}
                </Text>
              </Flex>
            ) : (
              enrolledmembers?.rows?.map((user: any, index: number) => (
                <Flex key={index} {...EnrolledCard}>
                  <Flex gap={3}>
                    <Box {...AvatarBox}>
                      <StyledProfileAvatar
                        name={user?.organizationMembership?.fullName || ""}
                        src={user?.organizationMembership?.avatar}
                        width="100%"
                        height="100%"
                      />
                      {/* <img
                        src={user?.organizationMembership?.avatar}
                        alt={user?.organizationMembership?.fullName}
                        width="100%"
                        height="100%"
                      /> */}
                    </Box>

                    <Box>
                      <Text fontSize="sm" fontWeight="600" mb={1}>
                        {user?.organizationMembership?.fullName}
                      </Text>
                      <Box w="200px">
                        <Box {...ProgressBackground}>
                          <Box {...ProgressFill(user?.progress)} />
                        </Box>
                      </Box>
                    </Box>
                  </Flex>

                  <Box textAlign="right">
                    {user?.progress === 100 && (
                      <Button
                        fontSize="14px"
                        px={2}
                        py={0.5}
                        color="#1819B4"
                        borderRadius="full"
                        fontWeight="500"
                        display="inline-flex"
                        alignItems="center"
                        gap={1}
                        w="fit-content"
                        bg="transparent"
                        h="auto"
                        minH="auto"
                        _hover={{
                          bg: "#F3F4F6",
                        }}
                        onClick={() =>
                          fetchCertificate({
                            courseId: selectedCourse?.course?.id,
                            subscriberId: userid,
                            userId: user?.organizationMembership?.userId,
                            gmtOffset: 0,
                            journalistId: user?.organizationMembership?.id,
                          })
                        }
                        isLoading={isCourseLoading(selectedCourse?.course?.id)}
                        disabled={isCourseLoading(selectedCourse?.course?.id)}
                      >
                        <DownloadIcon color="#1819B4" />
                        {t("certificateSection.download")}
                      </Button>
                    )}

                    <Text
                      as={Flex}
                      alignItems="center"
                      fontSize="xs"
                      fontWeight="500"
                      px={2}
                      py={0.5}
                      borderRadius="full"
                      display="inline-flex"
                      mb={2}
                      bg={
                        user?.progress === 100
                          ? "#D1FAE5"
                          : user?.progress < 100
                            ? "#FEF3C7"
                            : "#F3F4F6"
                      }
                      color={
                        user?.progress === 100
                          ? "#065F46"
                          : user?.progress < 100
                            ? "#92400E"
                            : "#6B7280"
                      }
                    >
                      {/* Dot */}
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg={
                          user?.progress === 100
                            ? "#10B981" // green
                            : user?.progress < 100
                              ? "#F97316" // orange
                              : "#9CA3AF"
                        }
                        mr={2}
                      />
                      {user?.progress < 100
                        ? t("memberDetailsDrawer.inProgress")
                        : t("certificateSection.completed")}
                    </Text>

                    <Text fontSize="xs" mt={1} color="#2F2F34" fontWeight="500">
                      {user?.progress}% {t("certificateSection.completed")}
                    </Text>
                  </Box>
                </Flex>
              ))
            )}
          </Box>
        </Flex>
      )}
    </Flex>
  );
};
export default SubscriberCourses;
